import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, Broadcast, Eye, SpeakerHigh, MagicWand, CheckCircle, BookmarkSimple, Lightning, FloppyDiskBack } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const emptyStage = () => ({
    person_name: '',
    target_type: 'gsm',
    phone: '',
    sip_extension: '',
    ring_seconds: 25,
    announcement_text: '',
});

const DAY_NAMES = ['Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'];

const emptyDigit = (digit) => ({
    digit,
    label: '',
    enabled: true,
    stages: [emptyStage()],
    final_fallback_text: 'Bohužel se nepodařilo nikoho zastihnout, zavolejte prosím později.',
});

const IvrMenu = () => {
    const [config, setConfig] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [applying, setApplying] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState('');
    const [generatingKey, setGeneratingKey] = useState(null);  // e.g. "1:2" or "1:final"
    const [audioMap, setAudioMap] = useState({});  // filename -> meta
    const [profiles, setProfiles] = useState([]);
    const [newProfileName, setNewProfileName] = useState('');
    const { toast } = useToast();

    const loadAudio = async () => {
        try {
            const r = await api.get('/ivr-menu/audio');
            const m = {};
            (r.data || []).forEach(a => { m[a.id] = a; });
            setAudioMap(m);
        } catch (_) { /* ignore audio list errors */ }
    };

    const generateBatch = async (force = false) => {
        setGeneratingKey('batch');
        try {
            const url = force ? '/ivr-menu/generate-tts-batch?force=true' : '/ivr-menu/generate-tts-batch';
            const r = await api.post(url);
            const { total_attempted, succeeded, failed } = r.data;
            toast({
                title: 'Hromadná TTS generace dokončena',
                description: `${succeeded}/${total_attempted} úspěch${failed ? `, ${failed} chyb` : ''}. Sync na Asterisk do 1 min.`,
            });
            await loadAudio();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setGeneratingKey(null);
        }
    };

    const generateTTS = async (slotOrDigit, stageIndex, text) => {
        if (!text || !text.trim()) {
            toast({ title: 'Prázdný text', description: 'Vyplňte text hlášení nejdřív.', variant: 'destructive' });
            return;
        }
        // slotOrDigit can be a digit "1".."9" OR a slot string. Global/named
        // slots: "welcome", "menu_prompt", "final_fallback", "after_hours_*"
        const SLOT_NAMES = ['welcome', 'menu_prompt', 'final_fallback'];
        const isSlot = typeof slotOrDigit === 'string'
            && (slotOrDigit.startsWith('after_hours_') || SLOT_NAMES.includes(slotOrDigit));
        const body = isSlot
            ? { slot: slotOrDigit, stage_index: stageIndex, text, voice: 'alloy', model: 'tts-1' }
            : { digit: slotOrDigit, stage_index: stageIndex, text, voice: 'alloy', model: 'tts-1' };
        const key = isSlot
            ? `${slotOrDigit}:${stageIndex ?? 'x'}`
            : `${slotOrDigit}:${stageIndex === null ? 'final' : stageIndex}`;
        setGeneratingKey(key);
        try {
            const r = await api.post('/ivr-menu/generate-tts', body);
            toast({
                title: 'Audio vygenerováno',
                description: `${r.data.filename} (${Math.round(r.data.size_bytes / 1024)} kB). Sync na Asterisk do 1 min.`,
            });
            await loadAudio();
        } catch (e) {
            toast({ title: 'TTS chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setGeneratingKey(null);
        }
    };

    const uploadCustomAudio = async (file, slot, digit, stageIndex) => {        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        if (slot) fd.append('slot', slot);
        if (digit) fd.append('digit', digit);
        if (stageIndex !== null && stageIndex !== undefined) fd.append('stage_index', String(stageIndex));
        const key = `upload:${slot || digit}:${stageIndex ?? 'x'}`;
        setGeneratingKey(key);
        try {
            const r = await api.post('/ivr-menu/audio/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast({
                title: 'WAV nahrán',
                description: `${r.data.filename} (${Math.round(r.data.size_bytes / 1024)} kB).`,
            });
            await loadAudio();
        } catch (e) {
            toast({ title: 'Chyba uploadu', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setGeneratingKey(null);
        }
    };

    const load = async () => {
        const r = await api.get('/ivr-menu');
        setConfig(r.data);
        setDirty(false);
        await loadAudio();
    };
    useEffect(() => { load(); loadProfiles(); }, []);

    const patch = (changes) => {
        setConfig(prev => ({ ...prev, ...changes }));
        setDirty(true);
    };

    const patchDigit = (idx, changes) => {
        const next = [...config.digits];
        next[idx] = { ...next[idx], ...changes };
        patch({ digits: next });
    };

    const patchStage = (digitIdx, stageIdx, changes) => {
        const stages = [...config.digits[digitIdx].stages];
        stages[stageIdx] = { ...stages[stageIdx], ...changes };
        patchDigit(digitIdx, { stages });
    };

    const addStage = (digitIdx) => {
        const stages = [...config.digits[digitIdx].stages, emptyStage()];
        patchDigit(digitIdx, { stages });
    };

    const removeStage = (digitIdx, stageIdx) => {
        const stages = config.digits[digitIdx].stages.filter((_, i) => i !== stageIdx);
        patchDigit(digitIdx, { stages });
    };

    const moveStage = (digitIdx, stageIdx, delta) => {
        const stages = [...config.digits[digitIdx].stages];
        const j = stageIdx + delta;
        if (j < 0 || j >= stages.length) return;
        [stages[stageIdx], stages[j]] = [stages[j], stages[stageIdx]];
        patchDigit(digitIdx, { stages });
    };

    const [testingKey, setTestingKey] = useState(null);
    const testStageCall = async (digit, sIdx, stage) => {
        const key = `${digit}-${sIdx}`;
        setTestingKey(key);
        try {
            const r = await api.post('/ivr-menu/test-stage-call', {
                target_type: stage.target_type || 'gsm',
                sip_extension: stage.sip_extension || null,
                phone: stage.phone || null,
                ring_seconds: stage.ring_seconds || 25,
                label: `Test stage ${digit}/${sIdx + 1}: ${stage.person_name || 'unnamed'}`,
            });
            toast({
                title: r.data.ok ? 'Test hovor odeslán' : 'AMI odpověděl s chybou',
                description: r.data.ok
                    ? `Channel: ${r.data.channel}. ${r.data.tip || ''}`
                    : `${r.data.ami_response}: ${r.data.ami_message}`,
                variant: r.data.ok ? 'default' : 'destructive',
            });
        } catch (e) {
            toast({ title: 'Test selhal', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setTestingKey(null);
        }
    };


    const addDigit = () => {
        const used = new Set(config.digits.map(d => d.digit));
        const next = ['1','2','3','4','5','6','7','8','9','0'].find(d => !used.has(d));
        if (!next) return;
        patch({ digits: [...config.digits, emptyDigit(next)] });
    };

    const removeDigit = (idx) => {
        patch({ digits: config.digits.filter((_, i) => i !== idx) });
    };

    const save = async () => {
        try {
            const body = {
                welcome_text: config.welcome_text || '',
                menu_prompt_text: config.menu_prompt_text || '',
                digits: config.digits,
                timezone: config.timezone || 'Europe/Prague',
                opening_hours: config.opening_hours || [],
                after_hours: config.after_hours || {},
            };
            await api.put('/ivr-menu', body);
            toast({ title: 'Uloženo', description: 'Konfigurace IVR uložena. Klikněte "Aplikovat na PBX" pro nasazení.' });
            load();
        } catch (e) {
            toast({ title: 'Chyba uložení', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    // ---- Opening Hours helpers ----
    const patchOpenHour = (idx, changes) => {
        const next = [...(config.opening_hours || [])];
        next[idx] = { ...next[idx], ...changes };
        patch({ opening_hours: next });
    };

    // ---- After-hours helpers ----
    const patchAfterHours = (changes) => {
        patch({ after_hours: { ...(config.after_hours || {}), ...changes } });
    };
    const patchAfterStage = (sIdx, changes) => {
        const stages = [...((config.after_hours || {}).human_cascade || [])];
        stages[sIdx] = { ...stages[sIdx], ...changes };
        patchAfterHours({ human_cascade: stages });
    };
    const addAfterStage = () => {
        const stages = [...((config.after_hours || {}).human_cascade || []), emptyStage()];
        patchAfterHours({ human_cascade: stages });
    };
    const removeAfterStage = (sIdx) => {
        const stages = ((config.after_hours || {}).human_cascade || []).filter((_, i) => i !== sIdx);
        patchAfterHours({ human_cascade: stages });
    };
    const moveAfterStage = (sIdx, delta) => {
        const stages = [...((config.after_hours || {}).human_cascade || [])];
        const j = sIdx + delta;
        if (j < 0 || j >= stages.length) return;
        [stages[sIdx], stages[j]] = [stages[j], stages[sIdx]];
        patchAfterHours({ human_cascade: stages });
    };

    const apply = async () => {
        setApplying(true);
        try {
            const r = await api.post('/ivr-menu/apply');
            toast({
                title: 'Aplikováno',
                description: `Vygenerováno ${r.data.rendered_chars} znaků dialplanu. Cron na VPS jej během 5 minut nasadí na Asterisk.`,
            });
            load();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setApplying(false);
        }
    };

    const showPreview = async () => {
        try {
            const r = await api.get('/ivr-menu/preview-dialplan');
            setPreview(r.data.dialplan);
            setPreviewOpen(true);
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    // ---- IVR Profiles ----
    const loadProfiles = async () => {
        try {
            const r = await api.get('/ivr-menu/profiles');
            setProfiles(r.data || []);
        } catch (_) { /* ignore profile list errors */ }
    };

    const createProfile = async () => {
        const name = (newProfileName || '').trim();
        if (!name) {
            toast({ title: 'Zadejte název profilu', variant: 'destructive' });
            return;
        }
        try {
            await api.post('/ivr-menu/profiles', { name });
            toast({ title: 'Profil uložen', description: `"${name}" obsahuje aktuální IVR konfiguraci.` });
            setNewProfileName('');
            await loadProfiles();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const activateProfile = async (id, name) => {
        if (dirty && !window.confirm(`Máte neuložené změny. Aktivace profilu "${name}" je zahodí. Pokračovat?`)) return;
        try {
            const r = await api.post(`/ivr-menu/profiles/${id}/activate`);
            toast({
                title: `Profil "${name}" aktivován`,
                description: `Dialplan přegenerován (${r.data.rendered_chars} znaků). Asterisk se aktualizuje do 5 min.`,
            });
            await Promise.all([load(), loadProfiles()]);
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const overwriteProfile = async (id, name) => {
        if (!window.confirm(`Přepsat profil "${name}" aktuální konfigurací? Předchozí snapshot bude smazán.`)) return;
        try {
            await api.put(`/ivr-menu/profiles/${id}/save-current`);
            toast({ title: 'Profil přepsán', description: `"${name}" nyní obsahuje aktuální konfiguraci.` });
            await loadProfiles();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const deleteProfile = async (id, name) => {
        if (!window.confirm(`Smazat profil "${name}"? Tato akce je nevratná.`)) return;
        try {
            await api.delete(`/ivr-menu/profiles/${id}`);
            toast({ title: 'Profil smazán', description: name });
            await loadProfiles();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    if (!config) {
        return <div className="p-8">Načítám…</div>;
    }

    return (
        <div className="space-y-6" data-testid="ivr-menu-page">
            <PageHeader
                eyebrow="Inbound Call Routing"
                title="IVR Menu — kaskádové směrování"
                description="Po volbě v IVR systém zazvoní postupně všem v kaskádě. Pokud první nezvedne, přejde na druhého (s ohlášením), atd."
                actions={
                    <div className="flex gap-2 flex-wrap">
                        <button
                            data-testid="ivr-menu-batch-tts-btn"
                            disabled={generatingKey === 'batch'}
                            onClick={generateBatch}
                            className="h-9 px-3 border text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                            title="Vygeneruje WAV soubory pro VŠECHNY texty, které nemají audio nebo mají odlišný text od poslední generace"
                        >
                            <MagicWand size={14} />
                            {generatingKey === 'batch' ? 'Generuji…' : 'Vygenerovat všechna audio'}
                        </button>
                        <button
                            data-testid="ivr-menu-preview-btn"
                            onClick={showPreview}
                            className="h-9 px-3 border text-sm inline-flex items-center gap-1.5"
                            style={{ borderColor: 'var(--border-default)' }}
                        >
                            <Eye size={14} /> Náhled dialplanu
                        </button>
                        <button
                            data-testid="ivr-menu-save-btn"
                            disabled={!dirty}
                            onClick={save}
                            className="h-9 px-3 border text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ borderColor: 'var(--border-default)', background: dirty ? 'var(--brand)' : 'transparent', color: dirty ? '#000' : 'inherit' }}
                        >
                            <FloppyDisk size={14} /> Uložit
                        </button>
                        <button
                            data-testid="ivr-menu-apply-btn"
                            disabled={applying || dirty}
                            onClick={apply}
                            className="h-9 px-3 border text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                            title={dirty ? 'Nejprve uložte změny' : 'Aplikovat na produkční Asterisk PBX'}
                        >
                            <Broadcast size={14} /> {applying ? 'Aplikuji…' : 'Aplikovat na PBX'}
                        </button>
                    </div>
                }
            />

            <Card title="Profily konfigurace (rychlé přepínání scénářů)" testId="ivr-profiles-card">
                <div className="p-4">
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                        Uložte aktuální nastavení IVR jako pojmenovaný profil (např. <strong>Normální</strong>,
                        <strong> Letní dovolená</strong>, <strong> Pohotovost noční směna</strong>). Aktivace
                        profilu jedním klikem přepíše živou konfiguraci a okamžitě přegeneruje dialplan.
                    </p>

                    <div className="flex items-center gap-2 mb-4">
                        <input
                            data-testid="ivr-profile-name-input"
                            type="text"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') createProfile(); }}
                            placeholder="Název nového profilu (např. Letní dovolená)"
                            maxLength={80}
                            className="flex-1 px-2 py-1.5 text-sm border font-mono"
                            style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', borderRadius: 2 }}
                        />
                        <button
                            data-testid="ivr-profile-create-btn"
                            onClick={createProfile}
                            disabled={!newProfileName.trim() || dirty}
                            className="h-8 px-3 text-xs font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: 'var(--brand)', color: '#000', borderRadius: 2 }}
                            title={dirty ? 'Nejprve uložte aktuální změny tlačítkem Uložit' : 'Uložit aktuální konfiguraci jako nový profil'}
                        >
                            <Plus size={12} weight="bold" /> Uložit aktuální jako profil
                        </button>
                    </div>

                    {profiles.length === 0 ? (
                        <div className="text-xs text-center py-6" style={{ color: 'var(--text-secondary)' }}>
                            Zatím žádné profily uloženy. Pojmenujte výše aktuální nastavení a klikněte na tlačítko „Uložit jako profil&quot;.
                        </div>
                    ) : (
                        <div className="grid gap-2">
                            {profiles.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`ivr-profile-${p.id}`}
                                    className="flex items-center gap-3 border p-2.5"
                                    style={{
                                        borderColor: p.is_active ? 'var(--brand)' : 'var(--border-default)',
                                        background: p.is_active ? 'rgba(34,197,94,0.06)' : 'var(--bg-elevated)',
                                        borderRadius: 2,
                                    }}
                                >
                                    <BookmarkSimple size={16} weight={p.is_active ? 'fill' : 'regular'}
                                                    color={p.is_active ? 'var(--brand)' : 'var(--text-secondary)'} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                                                {p.name}
                                            </span>
                                            {p.is_active && (
                                                <span data-testid={`ivr-profile-${p.id}-active-badge`}
                                                      className="text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5"
                                                      style={{ background: 'var(--brand)', color: '#000', borderRadius: 2 }}>
                                                    Aktivní
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] font-mono mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                                            {p.last_activated_at
                                                ? `Naposledy aktivován ${new Date(p.last_activated_at).toLocaleString('cs-CZ')}`
                                                : `Vytvořen ${p.created_at ? new Date(p.created_at).toLocaleString('cs-CZ') : '—'}`}
                                            {p.created_by && <> · {p.created_by}</>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {!p.is_active && (
                                            <button
                                                data-testid={`ivr-profile-${p.id}-activate-btn`}
                                                onClick={() => activateProfile(p.id, p.name)}
                                                className="h-7 px-2 text-[11px] font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1"
                                                style={{ background: 'var(--brand)', color: '#000', borderRadius: 2 }}
                                                title="Přepnout živou IVR konfiguraci na tento profil"
                                            >
                                                <Lightning size={12} weight="bold" /> Aktivovat
                                            </button>
                                        )}
                                        <button
                                            data-testid={`ivr-profile-${p.id}-overwrite-btn`}
                                            onClick={() => overwriteProfile(p.id, p.name)}
                                            disabled={dirty}
                                            className="h-7 px-2 text-[11px] uppercase tracking-[0.1em] inline-flex items-center gap-1 border disabled:opacity-50"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                            title={dirty ? 'Nejprve uložte aktuální změny' : 'Přepsat tento profil aktuální konfigurací'}
                                        >
                                            <FloppyDiskBack size={11} /> Přepsat
                                        </button>
                                        <button
                                            data-testid={`ivr-profile-${p.id}-delete-btn`}
                                            onClick={() => deleteProfile(p.id, p.name)}
                                            disabled={p.is_active}
                                            className="h-7 px-2 text-[11px] uppercase tracking-[0.1em] inline-flex items-center gap-1 border disabled:opacity-30"
                                            style={{ borderColor: 'var(--danger)', color: 'var(--danger)', borderRadius: 2 }}
                                            title={p.is_active ? 'Aktivní profil nelze smazat — nejprve aktivujte jiný' : 'Smazat profil'}
                                        >
                                            <Trash size={11} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            <Card title="Globální texty IVR (TTS)">
                <div className="p-4 grid gap-4">
                    {/* Voice + Model picker for entire IVR */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border" 
                         style={{ borderColor: 'var(--border-default)', background: 'rgba(59,130,246,0.04)', borderRadius: 2 }}>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.15em] font-bold block mb-1"
                                   style={{ color: 'var(--text-secondary)' }}>
                                Hlas (pro všechny TTS)
                            </label>
                            <select
                                data-testid="ivr-global-voice-picker"
                                value={config.tts_voice || 'nova'}
                                onChange={e => patch({ tts_voice: e.target.value })}
                                className="w-full px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            >
                                <optgroup label="Ženské">
                                    <option value="nova">Nova — žena (přirozená, doporučeno)</option>
                                    <option value="shimmer">Shimmer — žena (jemná, energická)</option>
                                    <option value="coral">Coral — žena (živá)</option>
                                    <option value="sage">Sage — žena (klidná)</option>
                                </optgroup>
                                <optgroup label="Mužské">
                                    <option value="onyx">Onyx — muž (hluboký, autorita)</option>
                                    <option value="echo">Echo — muž (neutrální)</option>
                                    <option value="ash">Ash — muž (mladý, dynamický)</option>
                                    <option value="fable">Fable — muž (vyprávěč)</option>
                                </optgroup>
                                <optgroup label="Neutrální">
                                    <option value="alloy">Alloy — neutrální (robotičtější)</option>
                                </optgroup>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.15em] font-bold block mb-1"
                                   style={{ color: 'var(--text-secondary)' }}>
                                Kvalita modelu
                            </label>
                            <select
                                data-testid="ivr-global-model-picker"
                                value={config.tts_model || 'tts-1-hd'}
                                onChange={e => patch({ tts_model: e.target.value })}
                                className="w-full px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            >
                                <option value="tts-1-hd">tts-1-hd — HD kvalita (čistá čeština, doporučeno)</option>
                                <option value="tts-1">tts-1 — Standard (rychlejší, levnější)</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                data-testid="ivr-regenerate-all-with-new-voice"
                                onClick={() => generateBatch(true)}
                                disabled={generatingKey === 'batch'}
                                className="w-full h-9 px-3 text-xs font-bold uppercase tracking-[0.08em] inline-flex items-center justify-center gap-1.5"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2, opacity: generatingKey === 'batch' ? 0.5 : 1 }}
                                title="Přegeneruje VŠECHNY audio (i ty co mají správný text) s nově zvoleným hlasem a modelem. Použijte po změně hlasu."
                            >
                                <MagicWand size={12} weight="bold" />
                                {generatingKey === 'batch' ? 'Generuji…' : 'Přegeneruj vše s tímto hlasem'}
                            </button>
                        </div>
                        <p className="text-[11px] md:col-span-3" style={{ color: 'var(--text-secondary)' }}>
                            Tip: Nejlepší čeština ze zkušenosti: <strong>Nova</strong> (žena) nebo <strong>Onyx</strong> (muž) s modelem <strong>tts-1-hd</strong>.
                            Nezapomeňte <strong>Uložit</strong> + <strong>Aplikovat na PBX</strong> nahoře.
                        </p>
                    </div>
                    {[
                        {
                            slot: 'welcome',
                            file: 'uvitani-solar-green.wav',
                            label: 'Uvítací text (Background uvítání)',
                            field: 'welcome_text',
                            testid: 'ivr-welcome',
                        },
                        {
                            slot: 'menu_prompt',
                            file: 'menu-volby.wav',
                            label: 'Text menu (po uvítání) — instrukce kterou klapku stisknout',
                            field: 'menu_prompt_text',
                            testid: 'ivr-menu',
                        },
                        {
                            slot: 'final_fallback',
                            file: 'final-fallback.wav',
                            label: 'Závěrečné hlášení (final-fallback) — když nikdo nezvedne / neodpoví',
                            field: 'global_final_fallback_text',
                            testid: 'ivr-final-fallback',
                            placeholder: '(Pokud necháte prázdné, použije se text z prvního digit menu.)',
                        },
                    ].map(({ slot, file, label, field, testid, placeholder }) => {
                        const meta = audioMap[file];
                        const text = (config[field] || '').trim();
                        const isStale = !!text && meta && meta.text && meta.text !== text && !meta.is_custom_upload;
                        return (
                            <div key={slot} className="border p-3" style={{ borderColor: 'var(--border-default)' }}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider"
                                           style={{ color: 'var(--text-secondary)' }}>
                                        {label}
                                    </label>
                                    {meta && (
                                        <StatusPill
                                            status={meta.is_custom_upload ? 'info' : (isStale ? 'warning' : 'success')}
                                            label={meta.is_custom_upload
                                                ? 'vlastní WAV'
                                                : (isStale ? 'text se změnil — regenerujte' : 'audio existuje')}
                                        />
                                    )}
                                </div>
                                <textarea
                                    data-testid={`${testid}-text`}
                                    value={config[field] || ''}
                                    onChange={e => patch({ [field]: e.target.value })}
                                    rows={2}
                                    placeholder={placeholder}
                                    className="w-full p-2 text-sm border"
                                    style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
                                />
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <button
                                        data-testid={`${testid}-generate-btn`}
                                        onClick={() => generateTTS(slot, null, config[field] || '')}
                                        disabled={generatingKey === `${slot}:x` || !text}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
                                        style={{
                                            background: 'var(--brand)',
                                            color: '#fff',
                                            borderRadius: 2,
                                            opacity: (generatingKey === `${slot}:x` || !text) ? 0.5 : 1,
                                        }}
                                    >
                                        <MagicWand size={12} weight="bold" />
                                        {generatingKey === `${slot}:x` ? 'Generuji…' : 'Generovat TTS'}
                                    </button>
                                    {meta && (
                                        <>
                                            <audio
                                                controls
                                                data-testid={`${testid}-preview`}
                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/ivr-menu/audio/${file}?v=${meta.generated_at || ''}`}
                                                className="h-7"
                                                style={{ maxWidth: 220 }}
                                            />
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL}/api/ivr-menu/audio/${file}`}
                                                download={file}
                                                data-testid={`${testid}-download-btn`}
                                                className="text-[11px] underline"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                Stáhnout
                                            </a>
                                        </>
                                    )}
                                    <label
                                        data-testid={`${testid}-upload-label`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] cursor-pointer border"
                                        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)', borderRadius: 2 }}
                                    >
                                        Nahrát WAV
                                        <input
                                            type="file"
                                            accept="audio/*,.wav,.mp3,.m4a,.ogg"
                                            className="hidden"
                                            data-testid={`${testid}-upload-input`}
                                            onChange={e => {
                                                const f = e.target.files?.[0];
                                                if (f) uploadCustomAudio(f, slot, null, null);
                                                e.target.value = '';
                                            }}
                                        />
                                    </label>
                                    {meta?.size_bytes && (
                                        <span className="text-[10px] font-mono" style={{ color: 'var(--text-secondary)' }}>
                                            {Math.round(meta.size_bytes / 1024)} kB
                                            {meta.generated_at ? ` · ${new Date(meta.generated_at).toLocaleString('cs-CZ')}` : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div className="flex items-center justify-between border-t pt-3"
                         style={{ borderColor: 'var(--border-default)' }}>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            Tip: <strong>&quot;Generovat vše&quot;</strong> dole pod stránkou přegeneruje i texty digit-menu a after-hours.
                        </p>
                        <a href="/ivr-audio"
                           data-testid="ivr-jump-to-audio-library"
                           className="text-[11px] underline font-bold uppercase tracking-[0.08em]"
                           style={{ color: 'var(--brand)' }}>
                            → Audio knihovna
                        </a>
                    </div>
                </div>
            </Card>

            <Card title="Otevírací hodiny (časové směrování)" testId="ivr-opening-hours-card">
                <div className="p-4">
                    <div className="flex flex-wrap items-center gap-3 mb-3 p-3 border"
                         style={{
                             background: config.after_hours_enabled === false
                                 ? 'rgba(239,68,68,0.06)'
                                 : 'rgba(34,197,94,0.06)',
                             borderColor: config.after_hours_enabled === false
                                 ? 'rgba(239,68,68,0.4)'
                                 : 'rgba(34,197,94,0.4)',
                             borderRadius: 2,
                         }}>
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5"
                                 style={{ color: config.after_hours_enabled === false ? '#ef4444' : '#22c55e' }}>
                                {config.after_hours_enabled === false
                                    ? 'After-hours směrování VYPNUTO'
                                    : 'After-hours směrování AKTIVNÍ'}
                            </div>
                            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {config.after_hours_enabled === false
                                    ? 'Otevírací hodiny ignorovány. Všechny hovory jedou standardním IVR (1/2/3).'
                                    : 'Hovory mimo otevírací dobu jdou na after-hours kaskádu (viz níže).'}
                            </div>
                        </div>
                        <button
                            data-testid="ivr-after-hours-toggle"
                            onClick={() => patch({ after_hours_enabled: !(config.after_hours_enabled !== false) })}
                            className="h-9 px-4 text-xs font-bold uppercase tracking-[0.15em] inline-flex items-center gap-1.5"
                            style={{
                                background: config.after_hours_enabled === false ? '#22c55e' : '#ef4444',
                                color: config.after_hours_enabled === false ? '#000' : '#fff',
                                borderRadius: 2,
                            }}
                            title="Zapnout/Vypnout směrování mimo pracovní dobu (vyžaduje Aplikovat dole)"
                        >
                            {config.after_hours_enabled === false ? '⚡ Zapnout' : '⏻ Vypnout'}
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <label className="text-xs font-bold uppercase tracking-[0.1em]"
                               style={{ color: 'var(--text-secondary)' }}>
                            Časové pásmo serveru
                        </label>
                        <select
                            data-testid="ivr-timezone-select"
                            value={config.timezone || 'Europe/Prague'}
                            onChange={(e) => patch({ timezone: e.target.value })}
                            className="px-2 py-1 text-sm border font-mono"
                            style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', borderRadius: 2 }}
                        >
                            <option value="Europe/Prague">Europe/Prague (CET/CEST)</option>
                            <option value="Europe/Bratislava">Europe/Bratislava</option>
                            <option value="Europe/Vienna">Europe/Vienna</option>
                            <option value="Europe/Berlin">Europe/Berlin</option>
                            <option value="Europe/Warsaw">Europe/Warsaw</option>
                            <option value="Europe/Budapest">Europe/Budapest</option>
                            <option value="Europe/London">Europe/London (GMT/BST)</option>
                            <option value="UTC">UTC</option>
                        </select>
                        <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                            Ovlivňuje vyhodnocení otevíracích hodin v Asterisk dialplanu a zobrazení
                            časů v CDR. Uložte tlačítkem dole.
                        </span>
                    </div>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                        Hovor mimo otevírací dobu jde na <strong>after-hours</strong> hlášení (viz níže).
                    </p>
                    <div className="grid gap-2">
                        {(config.opening_hours || []).map((rule, idx) => (
                            <div
                                key={idx}
                                data-testid={`opening-hours-${rule.weekday}`}
                                className="flex items-center gap-3 border p-2"
                                style={{ borderColor: 'var(--border-default)', background: 'var(--bg-elevated)' }}
                            >
                                <span className="w-20 font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {DAY_NAMES[rule.weekday] || `Den ${rule.weekday}`}
                                </span>
                                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input
                                        data-testid={`opening-hours-${rule.weekday}-enabled`}
                                        type="checkbox"
                                        checked={!!rule.enabled}
                                        onChange={e => patchOpenHour(idx, { enabled: e.target.checked })}
                                    />
                                    <span style={{ color: rule.enabled ? 'var(--brand)' : 'var(--text-secondary)' }}>
                                        {rule.enabled ? 'OTEVŘENO' : 'ZAVŘENO'}
                                    </span>
                                </label>
                                {rule.enabled && (
                                    <>
                                        <span className="text-xs ml-3" style={{ color: 'var(--text-secondary)' }}>od</span>
                                        <input
                                            data-testid={`opening-hours-${rule.weekday}-open`}
                                            type="time"
                                            value={rule.open_time || '09:00'}
                                            onChange={e => patchOpenHour(idx, { open_time: e.target.value })}
                                            className="px-2 py-1 text-sm border bg-transparent font-mono"
                                            style={{ borderColor: 'var(--border-default)' }}
                                        />
                                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>do</span>
                                        <input
                                            data-testid={`opening-hours-${rule.weekday}-close`}
                                            type="time"
                                            value={rule.close_time || '18:00'}
                                            onChange={e => patchOpenHour(idx, { close_time: e.target.value })}
                                            className="px-2 py-1 text-sm border bg-transparent font-mono"
                                            style={{ borderColor: 'var(--border-default)' }}
                                        />
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Card>

            {(() => {
                const ah = config.after_hours || {};
                const stages = ah.human_cascade || [];
                return (
                    <Card title="Mimo otevírací dobu (after-hours)" testId="ivr-after-hours-card">
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5"
                                       style={{ color: 'var(--text-secondary)' }}>
                                    Hlášení mimo otevírací dobu (TTS)
                                </label>
                                <div className="flex gap-1.5">
                                    <textarea
                                        data-testid="after-hours-msg-text"
                                        value={ah.message_text || ''}
                                        onChange={e => patchAfterHours({ message_text: e.target.value })}
                                        rows={2}
                                        className="flex-1 px-2 py-1.5 text-sm border bg-transparent"
                                        style={{ borderColor: 'var(--border-default)' }}
                                    />
                                    <button
                                        data-testid="after-hours-msg-tts"
                                        disabled={generatingKey === 'after_hours_msg:x' || !ah.message_text}
                                        onClick={() => generateTTS('after_hours_msg', null, ah.message_text)}
                                        className="h-8 px-2 border text-xs inline-flex items-center gap-1 disabled:opacity-30 flex-shrink-0 self-start"
                                        style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                                    >
                                        {generatingKey === 'after_hours_msg:x' ? '…' : <MagicWand size={12} />}
                                        {audioMap['ivr-after-hours-msg.wav']?.synced_to_asterisk
                                            ? <CheckCircle size={12} weight="fill" style={{ color: '#22c55e' }} /> : null}
                                    </button>
                                    {audioMap['ivr-after-hours-msg.wav'] && (
                                        <a data-testid="after-hours-msg-preview"
                                           href={`${process.env.REACT_APP_BACKEND_URL}/api/ivr-menu/audio/ivr-after-hours-msg.wav`}
                                           target="_blank" rel="noreferrer"
                                           className="h-8 px-2 border text-xs inline-flex items-center self-start"
                                           style={{ borderColor: 'var(--border-default)' }}>▶</a>
                                    )}
                                </div>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    WAV: <code>custom/ivr-after-hours-msg.wav</code>
                                </p>
                            </div>

                            <div className="flex items-center gap-2 border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                                <input
                                    data-testid="after-hours-allow-human"
                                    type="checkbox"
                                    checked={ah.allow_human_fallback !== false}
                                    onChange={e => patchAfterHours({ allow_human_fallback: e.target.checked })}
                                />
                                <span className="text-sm">Povolit přepojení na zástupce (volající stiskne libovolnou klapku)</span>
                            </div>

                            {ah.allow_human_fallback !== false && (
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider block mb-2"
                                           style={{ color: 'var(--text-secondary)' }}>
                                        Kaskáda lidských zástupců (po volbě nuly)
                                    </label>
                                    {stages.map((s, sIdx) => (
                                        <div key={sIdx} data-testid={`after-hours-stage-${sIdx}`}
                                             className="border p-3 mb-2"
                                             style={{ borderColor: 'var(--border-default)', background: 'var(--bg-elevated)' }}>
                                            <div className="flex items-start gap-3">
                                                <div className="font-mono font-bold w-7 h-7 flex items-center justify-center flex-shrink-0"
                                                     style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                                    {sIdx + 1}
                                                </div>
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                                                    <input data-testid={`after-hours-stage-${sIdx}-name`}
                                                           value={s.person_name} onChange={e => patchAfterStage(sIdx, { person_name: e.target.value })}
                                                           placeholder="Jméno"
                                                           className="px-2 py-1.5 text-sm border bg-transparent"
                                                           style={{ borderColor: 'var(--border-default)' }} />
                                                    <input data-testid={`after-hours-stage-${sIdx}-phone`}
                                                           value={s.phone} onChange={e => patchAfterStage(sIdx, { phone: e.target.value })}
                                                           placeholder="Číslo (420...)"
                                                           className="px-2 py-1.5 text-sm border bg-transparent font-mono"
                                                           style={{ borderColor: 'var(--border-default)' }} />
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Vyzvánění</span>
                                                        <input data-testid={`after-hours-stage-${sIdx}-ring`}
                                                               type="number" min={5} max={60}
                                                               value={s.ring_seconds}
                                                               onChange={e => patchAfterStage(sIdx, { ring_seconds: parseInt(e.target.value || '25', 10) })}
                                                               className="w-16 px-2 py-1.5 text-sm border bg-transparent"
                                                               style={{ borderColor: 'var(--border-default)' }} />
                                                        <span className="text-xs">s</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <button onClick={() => moveAfterStage(sIdx, -1)} disabled={sIdx === 0}
                                                            className="h-7 w-7 border flex items-center justify-center disabled:opacity-30"
                                                            style={{ borderColor: 'var(--border-default)' }}><ArrowUp size={12} /></button>
                                                    <button onClick={() => moveAfterStage(sIdx, 1)} disabled={sIdx === stages.length - 1}
                                                            className="h-7 w-7 border flex items-center justify-center disabled:opacity-30"
                                                            style={{ borderColor: 'var(--border-default)' }}><ArrowDown size={12} /></button>
                                                    <button onClick={() => removeAfterStage(sIdx)} disabled={stages.length <= 1}
                                                            className="h-7 w-7 border flex items-center justify-center text-red-500 disabled:opacity-30"
                                                            style={{ borderColor: 'var(--border-default)' }}><Trash size={12} /></button>
                                                </div>
                                            </div>
                                            {sIdx > 0 && (
                                                <div className="mt-3 pl-10">
                                                    <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-1"
                                                           style={{ color: 'var(--text-secondary)' }}>
                                                        <SpeakerHigh size={12} /> Hlášení před touto fází
                                                    </label>
                                                    <div className="flex gap-1.5">
                                                        <input data-testid={`after-hours-stage-${sIdx}-ann`}
                                                               value={s.announcement_text || ''}
                                                               onChange={e => patchAfterStage(sIdx, { announcement_text: e.target.value })}
                                                               placeholder='např. "Zkouším dalšího..."'
                                                               className="flex-1 px-2 py-1.5 text-sm border bg-transparent italic"
                                                               style={{ borderColor: 'var(--border-default)' }} />
                                                        <button data-testid={`after-hours-stage-${sIdx}-tts`}
                                                                disabled={generatingKey === `after_hours_stage:${sIdx + 1}` || !s.announcement_text}
                                                                onClick={() => generateTTS('after_hours_stage', sIdx + 1, s.announcement_text)}
                                                                className="h-8 px-2 border text-xs inline-flex items-center gap-1 disabled:opacity-30 flex-shrink-0"
                                                                style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}>
                                                            {generatingKey === `after_hours_stage:${sIdx + 1}` ? '…' : <MagicWand size={12} />}
                                                            {audioMap[`ivr-after-hours-stage-${sIdx + 1}.wav`]?.synced_to_asterisk
                                                                ? <CheckCircle size={12} weight="fill" style={{ color: '#22c55e' }} /> : null}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button data-testid="after-hours-add-stage"
                                            onClick={addAfterStage}
                                            className="h-8 px-3 border text-xs inline-flex items-center gap-1.5"
                                            style={{ borderColor: 'var(--border-default)' }}>
                                        <Plus size={12} /> Přidat zástupce do kaskády
                                    </button>
                                </div>
                            )}

                            <div className="pt-3 border-t" style={{ borderColor: 'var(--border-default)' }}>
                                <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5"
                                       style={{ color: 'var(--text-secondary)' }}>
                                    Finální hlášení po vyčerpání kaskády
                                </label>
                                <div className="flex gap-1.5">
                                    <input data-testid="after-hours-final-text"
                                           value={ah.final_message_text || ''}
                                           onChange={e => patchAfterHours({ final_message_text: e.target.value })}
                                           placeholder="Bohužel se nepodařilo nikoho zastihnout..."
                                           className="flex-1 px-2 py-1.5 text-sm border bg-transparent italic"
                                           style={{ borderColor: 'var(--border-default)' }} />
                                    <button data-testid="after-hours-final-tts"
                                            disabled={generatingKey === 'after_hours_final:x' || !ah.final_message_text}
                                            onClick={() => generateTTS('after_hours_final', null, ah.final_message_text)}
                                            className="h-8 px-2 border text-xs inline-flex items-center gap-1 disabled:opacity-30 flex-shrink-0"
                                            style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}>
                                        {generatingKey === 'after_hours_final:x' ? '…' : <MagicWand size={12} />}
                                        {audioMap['ivr-after-hours-final.wav']?.synced_to_asterisk
                                            ? <CheckCircle size={12} weight="fill" style={{ color: '#22c55e' }} /> : null}
                                    </button>
                                </div>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    WAV: <code>custom/ivr-after-hours-final.wav</code>
                                </p>
                            </div>
                        </div>
                    </Card>
                );
            })()}

            <div className="space-y-4">
                {config.digits.map((d, idx) => (
                    <Card
                        key={`${d.digit}-${idx}`}
                        testId={`ivr-digit-${d.digit}`}
                        title={
                            <div className="flex items-center gap-3">
                                <span
                                    className="inline-flex items-center justify-center font-mono font-bold w-9 h-9"
                                    style={{ background: 'var(--brand)', color: '#000', borderRadius: 2 }}
                                >
                                    {d.digit}
                                </span>
                                <input
                                    data-testid={`ivr-digit-${d.digit}-label`}
                                    value={d.label}
                                    onChange={e => patchDigit(idx, { label: e.target.value })}
                                    placeholder="Popisek volby (např. Obchod a Eshop)"
                                    className="flex-1 px-2 py-1 text-sm border bg-transparent"
                                    style={{ borderColor: 'var(--border-default)' }}
                                />
                                <StatusPill
                                    status={d.enabled ? 'success' : 'neutral'}
                                    label={d.enabled ? 'AKTIVNÍ' : 'VYPNUTO'}
                                />
                            </div>
                        }
                        action={
                            <div className="flex gap-1">
                                <button
                                    data-testid={`ivr-digit-${d.digit}-toggle`}
                                    onClick={() => patchDigit(idx, { enabled: !d.enabled })}
                                    className="h-8 px-2 border text-xs"
                                    style={{ borderColor: 'var(--border-default)' }}
                                >
                                    {d.enabled ? 'Vypnout' : 'Zapnout'}
                                </button>
                                <button
                                    data-testid={`ivr-digit-${d.digit}-delete`}
                                    onClick={() => removeDigit(idx)}
                                    className="h-8 px-2 border text-xs text-red-500"
                                    style={{ borderColor: 'var(--border-default)' }}
                                >
                                    <Trash size={12} />
                                </button>
                            </div>
                        }
                    >
                        <div className="p-4 space-y-3">
                            {d.stages.map((s, sIdx) => (
                                <div
                                    key={sIdx}
                                    data-testid={`ivr-digit-${d.digit}-stage-${sIdx}`}
                                    className="border p-3"
                                    style={{ borderColor: 'var(--border-default)', background: 'var(--bg-elevated)' }}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className="font-mono font-bold w-7 h-7 flex items-center justify-center flex-shrink-0"
                                            style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}
                                        >
                                            {sIdx + 1}
                                        </div>
                                        <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                                            <input
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-name`}
                                                value={s.person_name}
                                                onChange={e => patchStage(idx, sIdx, { person_name: e.target.value })}
                                                placeholder="Jméno (např. Evelin Kosan)"
                                                className="px-2 py-1.5 text-sm border bg-transparent"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            />
                                            <select
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-target-type`}
                                                value={s.target_type || 'gsm'}
                                                onChange={e => patchStage(idx, sIdx, { target_type: e.target.value })}
                                                className="px-2 py-1.5 text-sm border bg-transparent"
                                                style={{ borderColor: 'var(--border-default)' }}
                                                title="GSM = Fayn trunk na mobil. SIP = klapka v Zoiperu/Yak."
                                            >
                                                <option value="sip_extension">📞 SIP klapka (Zoiper/Yak)</option>
                                                <option value="gsm">📱 GSM mobilní (přes Fayn)</option>
                                            </select>
                                            {(s.target_type || 'gsm') === 'sip_extension' ? (
                                                <input
                                                    data-testid={`ivr-${d.digit}-stage-${sIdx}-sip-ext`}
                                                    value={s.sip_extension || ''}
                                                    onChange={e => patchStage(idx, sIdx, { sip_extension: e.target.value })}
                                                    placeholder="SIP klapka (např. 101)"
                                                    className="px-2 py-1.5 text-sm border bg-transparent font-mono"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                />
                                            ) : (
                                                <input
                                                    data-testid={`ivr-${d.digit}-stage-${sIdx}-phone`}
                                                    value={s.phone || ''}
                                                    onChange={e => patchStage(idx, sIdx, { phone: e.target.value })}
                                                    placeholder="Mobil bez + (např. 420608808815)"
                                                    className="px-2 py-1.5 text-sm border bg-transparent font-mono"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                />
                                            )}
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Vyzvánění</span>
                                                <input
                                                    data-testid={`ivr-${d.digit}-stage-${sIdx}-ring`}
                                                    type="number"
                                                    min={5}
                                                    max={60}
                                                    value={s.ring_seconds}
                                                    onChange={e => patchStage(idx, sIdx, { ring_seconds: parseInt(e.target.value || '25', 10) })}
                                                    className="w-16 px-2 py-1.5 text-sm border bg-transparent"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                />
                                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>s</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <button
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-test`}
                                                onClick={() => testStageCall(d.digit, sIdx, s)}
                                                disabled={testingKey === `${d.digit}-${sIdx}` ||
                                                          (s.target_type === 'sip_extension' ? !s.sip_extension : !s.phone)}
                                                className="h-7 px-2 border flex items-center justify-center text-[10px] font-bold uppercase disabled:opacity-30"
                                                style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                                                title="Vyšle test hovor přes AMI Originate na tento target. Pokud zvedne, slyšíte uvítání IVR."
                                            >
                                                {testingKey === `${d.digit}-${sIdx}` ? '…' : 'Test'}
                                            </button>
                                            <button
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-up`}
                                                onClick={() => moveStage(idx, sIdx, -1)}
                                                disabled={sIdx === 0}
                                                className="h-7 w-7 border flex items-center justify-center disabled:opacity-30"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            >
                                                <ArrowUp size={12} />
                                            </button>
                                            <button
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-down`}
                                                onClick={() => moveStage(idx, sIdx, 1)}
                                                disabled={sIdx === d.stages.length - 1}
                                                className="h-7 w-7 border flex items-center justify-center disabled:opacity-30"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            >
                                                <ArrowDown size={12} />
                                            </button>
                                            <button
                                                data-testid={`ivr-${d.digit}-stage-${sIdx}-delete`}
                                                onClick={() => removeStage(idx, sIdx)}
                                                disabled={d.stages.length <= 1}
                                                className="h-7 w-7 border flex items-center justify-center text-red-500 disabled:opacity-30"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            >
                                                <Trash size={12} />
                                            </button>
                                        </div>
                                    </div>
                                    {sIdx > 0 && (
                                        <div className="mt-3 pl-10">
                                            <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-1"
                                                   style={{ color: 'var(--text-secondary)' }}>
                                                <SpeakerHigh size={12} /> Hlášení před touto fází (Czech TTS)
                                            </label>
                                            <div className="flex gap-1.5">
                                                <input
                                                    data-testid={`ivr-${d.digit}-stage-${sIdx}-ann`}
                                                    value={s.announcement_text || ''}
                                                    onChange={e => patchStage(idx, sIdx, { announcement_text: e.target.value })}
                                                    placeholder='např. "Obchodník je nedostupný, přepojuji vás na zástupce."'
                                                    className="flex-1 px-2 py-1.5 text-sm border bg-transparent italic"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                />
                                                <button
                                                    data-testid={`ivr-${d.digit}-stage-${sIdx}-tts`}
                                                    disabled={generatingKey === `${d.digit}:${sIdx + 1}` || !s.announcement_text}
                                                    onClick={() => generateTTS(d.digit, sIdx + 1, s.announcement_text)}
                                                    className="h-8 px-2 border text-xs inline-flex items-center gap-1 disabled:opacity-30 flex-shrink-0"
                                                    style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                                                    title="Vygenerovat hlasovou nahrávku (OpenAI TTS, voice=alloy)"
                                                >
                                                    {generatingKey === `${d.digit}:${sIdx + 1}` ? '…' : <MagicWand size={12} />}
                                                    {audioMap[`ivr-stage-${d.digit}-${sIdx + 1}.wav`]?.synced_to_asterisk
                                                        ? <CheckCircle size={12} weight="fill" style={{ color: '#22c55e' }} />
                                                        : null}
                                                </button>
                                                {audioMap[`ivr-stage-${d.digit}-${sIdx + 1}.wav`] && (
                                                    <a
                                                        data-testid={`ivr-${d.digit}-stage-${sIdx}-preview`}
                                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/ivr-menu/audio/ivr-stage-${d.digit}-${sIdx + 1}.wav`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="h-8 px-2 border text-xs inline-flex items-center"
                                                        style={{ borderColor: 'var(--border-default)' }}
                                                        title="Přehrát preview"
                                                    >
                                                        ▶
                                                    </a>
                                                )}
                                            </div>
                                            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                                WAV: <code>custom/ivr-stage-{d.digit}-{sIdx + 1}.wav</code>
                                                {audioMap[`ivr-stage-${d.digit}-${sIdx + 1}.wav`]?.synced_to_asterisk
                                                    ? <span className="ml-2" style={{ color: '#22c55e' }}>· synced to Asterisk</span>
                                                    : audioMap[`ivr-stage-${d.digit}-${sIdx + 1}.wav`]
                                                        ? <span className="ml-2" style={{ color: '#f59e0b' }}>· sync pending (1 min)</span>
                                                        : <span className="ml-2 italic">· nevygenerováno</span>}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <button
                                data-testid={`ivr-${d.digit}-add-stage`}
                                onClick={() => addStage(idx)}
                                className="h-8 px-3 border text-xs inline-flex items-center gap-1.5"
                                style={{ borderColor: 'var(--border-default)' }}
                            >
                                <Plus size={12} /> Přidat další fázi (fallback)
                            </button>

                            <div className="pt-3 border-t" style={{ borderColor: 'var(--border-default)' }}>
                                <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5"
                                       style={{ color: 'var(--text-secondary)' }}>
                                    Finální hlášení (po vyčerpání všech fází)
                                </label>
                                <div className="flex gap-1.5">
                                    <input
                                        data-testid={`ivr-${d.digit}-final-text`}
                                        value={d.final_fallback_text || ''}
                                        onChange={e => patchDigit(idx, { final_fallback_text: e.target.value })}
                                        placeholder="např. Bohužel se nepodařilo nikoho zastihnout..."
                                        className="flex-1 px-2 py-1.5 text-sm border bg-transparent italic"
                                        style={{ borderColor: 'var(--border-default)' }}
                                    />
                                    <button
                                        data-testid={`ivr-${d.digit}-final-tts`}
                                        disabled={generatingKey === `${d.digit}:final` || !d.final_fallback_text}
                                        onClick={() => generateTTS(d.digit, null, d.final_fallback_text)}
                                        className="h-8 px-2 border text-xs inline-flex items-center gap-1 disabled:opacity-30 flex-shrink-0"
                                        style={{ borderColor: 'var(--brand)', color: 'var(--brand)' }}
                                        title="Vygenerovat finální hlášení (OpenAI TTS)"
                                    >
                                        {generatingKey === `${d.digit}:final` ? '…' : <MagicWand size={12} />}
                                        {audioMap[`ivr-fallback-${d.digit}.wav`]?.synced_to_asterisk
                                            ? <CheckCircle size={12} weight="fill" style={{ color: '#22c55e' }} />
                                            : null}
                                    </button>
                                    {audioMap[`ivr-fallback-${d.digit}.wav`] && (
                                        <a
                                            data-testid={`ivr-${d.digit}-final-preview`}
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/ivr-menu/audio/ivr-fallback-${d.digit}.wav`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="h-8 px-2 border text-xs inline-flex items-center"
                                            style={{ borderColor: 'var(--border-default)' }}
                                            title="Přehrát preview"
                                        >
                                            ▶
                                        </a>
                                    )}
                                </div>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    WAV: <code>custom/ivr-fallback-{d.digit}.wav</code>
                                    {audioMap[`ivr-fallback-${d.digit}.wav`]?.synced_to_asterisk
                                        ? <span className="ml-2" style={{ color: '#22c55e' }}>· synced to Asterisk</span>
                                        : audioMap[`ivr-fallback-${d.digit}.wav`]
                                            ? <span className="ml-2" style={{ color: '#f59e0b' }}>· sync pending (1 min)</span>
                                            : <span className="ml-2 italic">· nevygenerováno</span>}
                                </p>
                            </div>
                        </div>
                    </Card>
                ))}

                <button
                    data-testid="ivr-add-digit-btn"
                    onClick={addDigit}
                    disabled={config.digits.length >= 10}
                    className="h-10 w-full border-2 border-dashed text-sm inline-flex items-center justify-center gap-2 disabled:opacity-30"
                    style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
                >
                    <Plus size={14} /> Přidat další volbu (DTMF klapku)
                </button>
            </div>

            {previewOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-6"
                    style={{ background: 'rgba(0,0,0,0.7)' }}
                    onClick={() => setPreviewOpen(false)}
                >
                    <div
                        data-testid="ivr-preview-modal"
                        className="bg-black border max-w-4xl w-full max-h-[80vh] overflow-auto"
                        style={{ borderColor: 'var(--border-default)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-4 border-b flex items-center justify-between"
                             style={{ borderColor: 'var(--border-default)' }}>
                            <strong>Náhled extensions.conf (vygenerováno z konfigurace)</strong>
                            <button onClick={() => setPreviewOpen(false)} className="text-sm">×</button>
                        </div>
                        <pre className="p-4 text-xs font-mono whitespace-pre-wrap leading-relaxed"
                             style={{ color: 'var(--text-secondary)' }}>
                            {preview}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IvrMenu;
